// src/pages/Inventory.tsx
// Magazzino, con lo stesso percorso del mockup: la merce ENTRA scansionando,
// esce dalla scheda del prodotto (usata in servizio o venduta), lo storico
// racconta cos'è successo e il report riassume il mese.
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ScanBarcode, Boxes, History, BarChart3, Info } from 'lucide-react';

import InventoryScanTab from '@/components/inventory/InventoryScanTab';
import InventoryProductsTab from '@/components/inventory/InventoryProductsTab';
import InventoryHistoryTab from '@/components/inventory/InventoryHistoryTab';
import InventoryReportTab from '@/components/inventory/InventoryReportTab';

type InventoryTab = 'scan' | 'products' | 'history' | 'report';

const Inventory: React.FC = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<InventoryTab>('scan');
    // Dalla scansione di un codice nuovo si passa alla scheda del prodotto appena creato
    const [focusProductId, setFocusProductId] = useState<number | null>(null);

    const openProduct = (id: number) => {
        setFocusProductId(id);
        setActiveTab('products');
    };

    const tabs: { id: InventoryTab; label: string; icon: any }[] = [
        { id: 'scan', label: t('inventory.tabs.scan', 'Scansiona'), icon: ScanBarcode },
        { id: 'products', label: t('inventory.tabs.products', 'Prodotti'), icon: Boxes },
        { id: 'history', label: t('inventory.tabs.history', 'Storico'), icon: History },
        { id: 'report', label: t('inventory.tabs.report', 'Report'), icon: BarChart3 },
    ];

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {t('inventory.title', 'Magazzino')}
                </h1>
                <p className="text-gray-500 mt-1 text-sm sm:text-base">
                    {t('inventory.subtitle', 'Carico, consumi e vendite dei prodotti del salone')}
                </p>
            </div>

            <div className="bg-white rounded-2xl p-1.5 border border-gray-100 shadow-sm inline-flex flex-wrap gap-1 max-w-full">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === tab.id
                            ? 'bg-clip-500 text-white shadow-md shadow-clip-200'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                    >
                        <tab.icon size={16} />
                        {tab.label}
                    </button>
                ))}
            </div>

            <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
            >
                {activeTab === 'scan' && <InventoryScanTab onProductCreated={openProduct} />}
                {activeTab === 'products' && (
                    <InventoryProductsTab
                        focusProductId={focusProductId}
                        onFocusHandled={() => setFocusProductId(null)}
                    />
                )}
                {activeTab === 'history' && <InventoryHistoryTab />}
                {activeTab === 'report' && <InventoryReportTab />}
            </motion.div>
        </div>
    );
};

export default Inventory;
